import React, { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, postData } from "../../services/request"

const AdminPayoutDetails = () => {
  const { payoutId } = useParams()
  const navigate = useNavigate()
  const [payout, setPayout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [approving, setApproving] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [rejectionReason, setRejectionReason] = useState("")
  const [showRejectModal, setShowRejectModal] = useState(false)

  const fetchPayout = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/payouts/${payoutId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error == "") {
      if (res.result.status === 200) {
        setPayout(res.result.data?.data)
      } else {
        setError(res.result.data?.message || 'Failed to load payout')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  useEffect(() => {
    fetchPayout()
  }, [payoutId])

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this payout? The amount will be credited to the hospital's wallet.")) {
      return
    }

    setApproving(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/payouts/${payoutId}/approve`
    const res = await postData(url, {}, {})
    setApproving(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        fetchPayout()
        alert("Payout approved successfully!")
      } else {
        setError(res.result.data?.message || 'Failed to approve payout')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a rejection reason")
      return
    }

    setRejecting(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/payouts/${payoutId}/reject`
    const data = { rejectionReason }
    const res = await postData(url, {}, data)
    setRejecting(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setShowRejectModal(false)
        setRejectionReason("")
        fetchPayout()
        alert("Payout rejected successfully!")
      } else {
        setError(res.result.data?.message || 'Failed to reject payout')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  const formatCurrency = (amount, currency = 'NGN') => {
    const num = Number(amount)
    if (isNaN(num)) return amount
    return `${currency === 'NGN' ? '₦' : currency} ${num.toLocaleString()}`
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700'
      case 'approved':
        return 'bg-blue-100 text-blue-700'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'rejected':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="p-6 text-sm text-gray-500">Loading payout details...</div>
      </div>
    )
  }

  if (error && !payout) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
        <button
          onClick={() => navigate('/admin/app/payouts')}
          className="mt-4 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
        >
          Back to Payouts
        </button>
      </div>
    )
  }

  if (!payout) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="p-6 text-sm text-gray-500">Payout not found</div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payout Details</h1>
          <p className="mt-1 text-sm text-gray-500">Review and manage payout</p>
        </div>
        <button
          onClick={() => navigate('/admin/app/payouts')}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to Payouts
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Status Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Status</h2>
              <p className="mt-1 text-sm text-gray-500">Current payout status</p>
            </div>
            <span className={`rounded-full px-4 py-2 text-sm font-semibold ${getStatusColor(payout.status)}`}>
              {payout.status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </div>
        </div>

        {/* Amount Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Amount</h2>
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(payout.amount, payout.currency)}
          </div>
        </div>

        {/* Hospital & Patient Info */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Hospital</h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-500">Name:</span>
                <span className="ml-2 text-gray-900">
                  {payout.hospitalId?.organization || payout.hospitalId?.name || 'Unknown'}
                </span>
              </div>
              {payout.hospitalId?.email && (
                <div>
                  <span className="font-medium text-gray-500">Email:</span>
                  <span className="ml-2 text-gray-900">{payout.hospitalId.email}</span>
                </div>
              )}
              {payout.hospitalId?.phoneNumber && (
                <div>
                  <span className="font-medium text-gray-500">Phone:</span>
                  <span className="ml-2 text-gray-900">{payout.hospitalId.phoneNumber}</span>
                </div>
              )}
              {payout.hospitalId?._id && (
                <div className="pt-2">
                  <Link
                    to={`/admin/app/hospitals/${payout.hospitalId._id}`}
                    className="text-sm font-medium text-black hover:underline"
                  >
                    View Hospital Details
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Patient</h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-500">Name:</span>
                <span className="ml-2 text-gray-900">
                  {payout.patientId?.name || 'Unknown'}
                </span>
              </div>
              {payout.patientId?.email && (
                <div>
                  <span className="font-medium text-gray-500">Email:</span>
                  <span className="ml-2 text-gray-900">{payout.patientId.email}</span>
                </div>
              )}
              {payout.patientId?.phoneNumber && (
                <div>
                  <span className="font-medium text-gray-500">Phone:</span>
                  <span className="ml-2 text-gray-900">{payout.patientId.phoneNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {payout.postId && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Request Details</h2>
              <Link
                to={`/admin/app/requests/${payout.postId._id || payout.postId}`}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                View Full Request
              </Link>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Request ID:</span>
                <span className="ml-2 text-sm text-gray-900">{payout.postId._id || payout.postId || 'N/A'}</span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Message:</span>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                  {payout.postId.message || "Request message unavailable"}
                </p>
              </div>
              {payout.postId.status && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Status:</span>
                  <span className="ml-2 text-sm text-gray-900">{payout.postId.status}</span>
                </div>
              )}
              {payout.postId.createdAt && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Created:</span>
                  <span className="ml-2 text-sm text-gray-900">{formatDate(payout.postId.createdAt)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Info */}
        {payout.paymentId && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Payment Information</h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-500">Transaction ID:</span>
                <span className="ml-2 text-gray-900">{payout.paymentId.transactionId || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">Transaction Reference:</span>
                <span className="ml-2 text-gray-900">{payout.paymentId.txRef || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">Payment Status:</span>
                <span className="ml-2 text-gray-900">{payout.paymentId.status || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Approval/Rejection Info */}
        {payout.approvedBy && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Approval Information</h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-500">Approved By:</span>
                <span className="ml-2 text-gray-900">
                  {payout.approvedBy?.name || payout.approvedBy?.email || 'Unknown'}
                </span>
              </div>
              {payout.approvedAt && (
                <div>
                  <span className="font-medium text-gray-500">Approved At:</span>
                  <span className="ml-2 text-gray-900">{formatDate(payout.approvedAt)}</span>
                </div>
              )}
              {payout.paidAt && (
                <div>
                  <span className="font-medium text-gray-500">Paid At:</span>
                  <span className="ml-2 text-gray-900">{formatDate(payout.paidAt)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {payout.rejectedBy && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-red-900">Rejection Information</h2>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-red-700">Rejected By:</span>
                <span className="ml-2 text-red-900">
                  {payout.rejectedBy?.name || payout.rejectedBy?.email || 'Unknown'}
                </span>
              </div>
              {payout.rejectedAt && (
                <div>
                  <span className="font-medium text-red-700">Rejected At:</span>
                  <span className="ml-2 text-red-900">{formatDate(payout.rejectedAt)}</span>
                </div>
              )}
              {payout.rejectionReason && (
                <div>
                  <span className="font-medium text-red-700">Reason:</span>
                  <span className="ml-2 text-red-900">{payout.rejectionReason}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Timestamps</h2>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-gray-500">Created:</span>
              <span className="ml-2 text-gray-900">{formatDate(payout.createdAt)}</span>
            </div>
            {payout.updatedAt && (
              <div>
                <span className="font-medium text-gray-500">Last Updated:</span>
                <span className="ml-2 text-gray-900">{formatDate(payout.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {payout.status === 'pending' && (
          <div className="flex gap-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <button
              onClick={handleApprove}
              disabled={approving}
              className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {approving ? "Approving..." : "Approve Payout"}
            </button>
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={rejecting}
              className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reject Payout
            </button>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-xl font-bold text-gray-900">Reject Payout</h2>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Rejection Reason <span className="text-red-600">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection"
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectionReason("")
                  setError("")
                }}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejecting || !rejectionReason.trim()}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {rejecting ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPayoutDetails

